import { Link } from "react-router";
import {
  FundOutlined,
  DashboardOutlined,
  NodeIndexOutlined,
  PieChartOutlined,
  RiseOutlined,
  FileTextOutlined,
  ArrowRightOutlined,
  CheckOutlined,
  CloseOutlined,
  StarFilled,
} from "@ant-design/icons";
import "./HomePage.css";

const FEATURES = [
  {
    icon: <FundOutlined />,
    accent: "teal",
    title: "Budgeting & forecasting",
    text: "Industry-tailored models with 60+ line items — built with investor logic by default.",
  },
  {
    icon: <DashboardOutlined />,
    accent: "",
    title: "Performance dashboards",
    text: "25+ firm-wide and product KPIs tracked in real time — P&L, cash, runway, efficiency.",
  },
  {
    icon: <NodeIndexOutlined />,
    accent: "coral",
    title: "Scenario planning",
    text: "Model growth, cost structure, pricing, revenue, cost allocation, investor input, in seconds.",
  },
  {
    icon: <PieChartOutlined />,
    accent: "teal",
    title: "Cap table & dilution",
    text: "Integrated, not bolted on. Priced rounds, investor margin.",
  },
  {
    icon: <RiseOutlined />,
    accent: "",
    title: "Valuation & share pricing",
    text: "From pre-seed to Series A+ — DCF, venture-scale, share pricing per round, investor risk / reward.",
  },
  {
    icon: <FileTextOutlined />,
    accent: "coral",
    title: "Investor reports",
    text: "Auto-generated update templates and dashboards, IC pricing.",
  },
];

const COMPARE_ROWS = [
  { label: "Industry-specific starter models", ss: "no", generic: "partial", pn: "yes" },
  { label: "Investor logic embedded by default", ss: "no", generic: "no", pn: "yes" },
  { label: "Integrated valuation + cap table", ss: "no", generic: "no", pn: "yes" },
  { label: "Dynamic scenario engine", ss: "partial", generic: "partial", pn: "yes" },
  { label: "Auto-generated investor reports", ss: "no", generic: "no", pn: "yes" },
  { label: "Time-to-investor-grade output", ss: "Weeks", generic: "Days", pn: "Minutes" },
];

const STATS = [
  { value: "60+", label: "Financial line items", sub: "projected and traced" },
  { value: "25+", label: "KPIs tracked", sub: "firm-wide and product" },
  { value: "Minutes", label: "To a pitch-ready model", sub: "vs 3–6 weeks with consultants" },
  { value: "$0", label: "Consultant fees avoided", sub: "per fundraise cycle" },
];

const TESTIMONIALS = [
  {
    quote:
      "Replaced our finance consultant in 10 minutes. The Series A model PreNumbers produced was the one we actually used with investors.",
    name: "Priya R.",
    role: "Founder & CEO",
    tag: "Seed → Series A+",
  },
  {
    quote:
      "I run FP&A for 12 portfolio companies. Before PreNumbers, every deck told a different story. Now it's one language across the portfolio.",
    name: "Marcus T.",
    role: "Partner",
    tag: "Venture studio",
  },
  {
    quote:
      "Our IC stress-tests every deal in PreNumbers now. Valuation, dilution, scenarios — all in one surface instead of five spreadsheets.",
    name: "Dana L.",
    role: "Principal",
    tag: "Seed VC",
  },
];

function Donut({ percent, color }) {
  return (
    <div
      className="pn-donut"
      style={{ background: `conic-gradient(${color} ${percent}%, rgba(255,255,255,0.08) 0)` }}
    >
      <div className="pn-donut-hole" />
    </div>
  );
}

function CompareCell({ value }) {
  if (value === "yes")
    return (
      <span className="pn-cmp pn-cmp-yes">
        <CheckOutlined /> Yes
      </span>
    );
  if (value === "no")
    return (
      <span className="pn-cmp pn-cmp-no">
        <CloseOutlined /> No
      </span>
    );
  if (value === "partial") return <span className="pn-cmp pn-cmp-partial">Partial</span>;
  return <span className="pn-cmp pn-cmp-text">{value}</span>;
}

function Stars() {
  return (
    <span className="pn-stars">
      {[0, 1, 2, 3, 4].map((i) => (
        <StarFilled key={i} />
      ))}
    </span>
  );
}

function HomePage() {
  return (
    <div className="home pn-landing">
      {/* ===== HERO ===== */}
      <section className="pn-hero">
        <div className="pn-hero-glow" />
        <div className="pn-container pn-hero-inner">
          <span className="pn-badge">✦ New · Scenario engine for all key drivers</span>
          <h1 className="pn-hero-title">
            Investor-Native FP&amp;A Intelligence <span className="pn-gold">Platform</span>
          </h1>
          <p className="pn-hero-sub">
            Build, manage, and analyze investor-grade financials in minutes — not weeks.
            Industry-tailored projections, valuation, and cap tables, without consultants or
            spreadsheets.
          </p>

          <div className="pn-hero-cta">
            <div className="pn-cta-stack">
              <Link to="/pricing" className="pn-btn pn-btn-gold">
                Request a demo <ArrowRightOutlined />
              </Link>
              <span className="pn-cta-note">No credit card required</span>
            </div>
            <div className="pn-cta-stack">
              <Link to="/pricing" className="pn-btn pn-btn-dark">
                Start for $25/mo
              </Link>
              <span className="pn-cta-note">Starter plan for solo founders</span>
            </div>
          </div>

          {/* Dashboard mock */}
          <div className="pn-dashboard">
            <div className="pn-dashboard-bar">FP&amp;A, KPIs &amp; Equity management</div>
            <div className="pn-dashboard-body">
              <div className="pn-kpi-grid">
                <div className="pn-kpi pn-kpi-lg">
                  <span className="pn-kpi-label">Market Cap at IPO</span>
                  <strong className="pn-kpi-value">$192,418,687.97</strong>
                  <Donut percent={79} color="#5b8def" />
                </div>
                <div className="pn-kpi pn-kpi-lg">
                  <span className="pn-kpi-label">Total Investment</span>
                  <strong className="pn-kpi-value">$15,607,117.01</strong>
                  <Donut percent={62} color="#5fd0a0" />
                </div>
                <div className="pn-kpi pn-kpi-lg">
                  <span className="pn-kpi-label">Gross Margin</span>
                  <strong className="pn-kpi-value">79.35%</strong>
                  <Donut percent={79} color="#c9aa6c" />
                </div>

                <div className="pn-chart-wrap">
                  <div className="pn-chart-head">Revenue vs Cost</div>
                  <svg viewBox="0 0 240 90" className="pn-chart-svg" preserveAspectRatio="none">
                    {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                      <rect
                        key={i}
                        x={10 + i * 33}
                        y={70 - i * 7}
                        width="14"
                        height={i * 7 + 12}
                        rx="2"
                        fill="#5b8def"
                        opacity="0.85"
                      />
                    ))}
                    <polyline
                      points="17,60 50,52 83,40 116,30 149,34 182,20 215,10"
                      fill="none"
                      stroke="#e07a9b"
                      strokeWidth="2"
                    />
                  </svg>
                </div>

                {[
                  { v: "60.15%", l: "Net Income Growth", c: "#5b8def", p: 60 },
                  { v: "20.65%", l: "Op. Margin", c: "#5fd0a0", p: 20 },
                  { v: "67.5%", l: "Rule of 40", c: "#c9aa6c", p: 67 },
                  { v: "$255", l: "CAC", c: "#9b7bd4", p: 45 },
                  { v: "$2,696.48", l: "LTV", c: "#5b8def", p: 80 },
                  { v: "7.5%", l: "Churn", c: "#e07a9b", p: 7 },
                ].map((k, i) => (
                  <div className="pn-kpi pn-kpi-sm" key={i}>
                    <strong className="pn-kpi-value-sm">{k.v}</strong>
                    <span className="pn-kpi-label-sm">{k.l}</span>
                    <Donut percent={k.p} color={k.c} />
                  </div>
                ))}

                <div className="pn-chart-wrap pn-chart-line">
                  <div className="pn-chart-head">ARR Growth</div>
                  <svg viewBox="0 0 240 80" className="pn-chart-svg" preserveAspectRatio="none">
                    <polyline
                      points="10,70 50,62 90,50 130,38 170,22 210,8"
                      fill="none"
                      stroke="#5fd0a0"
                      strokeWidth="2.5"
                    />
                    <polyline
                      points="10,72 50,68 90,60 130,52 170,40 210,30"
                      fill="none"
                      stroke="#5b8def"
                      strokeWidth="2"
                      opacity="0.7"
                    />
                  </svg>
                </div>

                <div className="pn-table">
                  <div className="pn-table-title">Valuation at IPO</div>
                  {[
                    ["Market Cap at IPO", "$192.4M"],
                    ["P/E Ratio", "27.2"],
                    ["Investment Equity %", "5.77%"],
                    ["IRR Multiplier", "15.0x"],
                    ["Share Price", "$254.86"],
                  ].map(([k, v]) => (
                    <div className="pn-table-row" key={k}>
                      <span>{k}</span>
                      <span className="pn-table-val">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="pn-trust">
            <div className="pn-trust-left">
              <span className="pn-avatars">
                <i style={{ background: "#e07a9b" }} />
                <i style={{ background: "#c9aa6c" }} />
                <i style={{ background: "#5b8def" }} />
              </span>
              <span>
                Trusted by founders <em>Seed → Series A+</em>
              </span>
            </div>
            <div className="pn-trust-right">
              <Stars /> 4.9 by operators &amp; alums
            </div>
          </div>
        </div>
      </section>

      {/* ===== LOGOS ===== */}
      <section className="pn-logos">
        <div className="pn-container">
          <p className="pn-logos-label">Backed by operators from</p>
          <div className="pn-logos-row">
            {["Entrepreneurs", "Advisors", "Investors", "Startups", "CFOs", "Innovators"].map(
              (l) => (
                <span key={l}>{l}</span>
              )
            )}
          </div>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section className="pn-features">
        <div className="pn-container">
          <span className="pn-badge pn-badge-center">✦ The FP&amp;A Intelligence Layer</span>
          <h2 className="pn-section-title">
            Everything you need. Nothing you <span className="pn-gold">don't.</span>
          </h2>
          <p className="pn-section-sub">
            PreNumbers turns raw business assumptions into investor-grade financials —
            projections, valuation, cap table, scenarios — in one integrated surface.
          </p>
          <div className="pn-feature-grid">
            {FEATURES.map((f) => (
              <div className="pn-feature-card" key={f.title}>
                <span className={`pn-feature-icon ${f.accent}`}>{f.icon}</span>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== COMPARISON ===== */}
      <section className="pn-compare">
        <div className="pn-container">
          <span className="pn-badge pn-badge-center">✦ Category wedge</span>
          <h2 className="pn-section-title">
            Not accounting. Not generic <span className="pn-gold">FP&amp;A.</span>
          </h2>
          <p className="pn-section-sub">
            Most tools stop at: "here's your model — good luck explaining it to investors."
            PreNumbers keeps going: here's the model, the logic, the trade-offs, and the
            optimized outcome.
          </p>
          <div className="pn-compare-table">
            <div className="pn-compare-head">
              <span>Capability</span>
              <span>Spreadsheets</span>
              <span>Generic FP&amp;A tools</span>
              <span className="pn-compare-pn">PreNumbers</span>
            </div>
            {COMPARE_ROWS.map((r) => (
              <div className="pn-compare-row" key={r.label}>
                <span className="pn-compare-label">{r.label}</span>
                <span><CompareCell value={r.ss} /></span>
                <span><CompareCell value={r.generic} /></span>
                <span className="pn-compare-pn"><CompareCell value={r.pn} /></span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STATS + TESTIMONIALS + CTA (one continuous band) ===== */}
      <div className="pn-band-group">
      {/* ===== STATS ===== */}
      <section className="pn-stats">
        <div className="pn-container pn-stats-row">
          {STATS.map((s) => (
            <div className="pn-stat" key={s.label}>
              <strong className="pn-stat-value">{s.value}</strong>
              <span className="pn-stat-label">{s.label}</span>
              <span className="pn-stat-sub">{s.sub}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="pn-testimonials">
        <div className="pn-container pn-testimonial-grid">
          {TESTIMONIALS.map((t) => (
            <div className="pn-testimonial-card" key={t.name}>
              <Stars />
              <p className="pn-testimonial-quote">"{t.quote}"</p>
              <div className="pn-testimonial-meta">
                <div>
                  <strong>{t.name}</strong>
                  <span>{t.role}</span>
                </div>
                <span className="pn-testimonial-tag">{t.tag}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="pn-cta">
        <div className="pn-container">
          <div className="pn-cta-card">
            <div className="pn-cta-content">
              <span className="pn-badge">✦ Get started</span>
              <h2 className="pn-cta-title">
                See investor-grade FP&amp;A in <span className="pn-gold">minutes.</span>
              </h2>
              <p>
                A PreNumbers specialist walks you through an example of industry tailored model for
                your venture — projections, valuation, cap table, scenarios — live.
              </p>
            </div>
            <div className="pn-cta-actions">
              <Link to="/contact-sales" className="pn-btn pn-btn-gold">
                Request a demo <ArrowRightOutlined />
              </Link>
              <Link to="/pricing" className="pn-btn pn-btn-ghost">
                See pricing
              </Link>
              <span className="pn-cta-note">No credit card · 30-min call · investor-grade output</span>
            </div>
          </div>
        </div>
      </section>
      </div>
    </div>
  );
}

export default HomePage;
